import * as Sentry from '@sentry/react';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment: boolean;
  private sink: Console | undefined;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.sink = globalThis['console'];
  }

  /**
   * Debug level logging - only in development
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      this.sink?.debug?.('[DEBUG]', message, context || '');
    }
  }

  /**
   * Info level logging - only in development
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      this.sink?.info?.('[INFO]', message, context || '');
    }
  }

  /**
   * Warning level logging - development only, sent to Sentry in production
   */
  warn(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      this.sink?.warn?.('[WARN]', message, context || '');
    } else {
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: context,
      });
    }
  }

  /**
   * Error level logging - development console, Sentry in production
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (this.isDevelopment) {
      this.sink?.error?.('[ERROR]', message, error, context || '');
    } else {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          extra: {
            message,
            ...context,
          },
        });
      } else {
        Sentry.captureMessage(message, {
          level: 'error',
          extra: {
            error,
            ...context,
          },
        });
      }
    }
  }

  /**
   * Set user context for error tracking
   */
  setUser(user: { id?: string; email?: string; username?: string }): void {
    if (!this.isDevelopment) {
      Sentry.setUser(user);
    }
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    if (!this.isDevelopment) {
      Sentry.setUser(null);
    }
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(message: string, category?: string, data?: LogContext): void {
    if (!this.isDevelopment) {
      Sentry.addBreadcrumb({
        message,
        category: category || 'custom',
        data,
        level: 'info',
      });
    } else {
      this.sink?.info?.('[BREADCRUMB]', category || 'custom', message, data || '');
    }
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;
